import React from 'react';

/**
 * SB-10384 — label for one hour of the agenda's time axis. The server formats
 * `hourLabels` (0..23) with the embed's hour pattern and locale, the same
 * pattern the session cards use, so a 24-hour embed reads "13:00" on both.
 * The 12-hour fallback only covers a cached payload from before that field.
 * @param {number} hour 0..23
 * @param {string[] | undefined} hourLabels
 * @returns {string}
 */
export const hourAxisLabel = (hour, hourLabels) => {
  const label = Array.isArray(hourLabels) ? hourLabels[hour] : null;
  if (typeof label === 'string' && label) return label;
  return hour === 12 ? '12:00 PM' : hour > 12 ? `${hour - 12}:00 PM` : hour === 0 ? '12:00 AM' : `${hour}:00 AM`;
};

export default function TimeGrid({ startHour, endHour, hourLabels, children }) {
  const hours = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    hours.push({ hour, timeString: hourAxisLabel(hour, hourLabels) });
  }

  return (
    <div className="time-grid">
      {/* Time labels */}
      <div className="time-grid__labels">
        {hours.map(({ hour, timeString }) => (
          <div key={hour} className="time-grid__time-slot">
            {timeString}
          </div>
        ))}
      </div>

      {/* Grid content */}
      <div className="time-grid__content">
        {/* Grid lines */}
        <div className="time-grid__lines">
          {hours.map(({ hour }, index) => (
            <div key={hour} className={`time-grid__line ${index === 0 ? 'time-grid__line--first' : ''}`} />
          ))}
        </div>

        {children}
      </div>
    </div>
  );
}

/**
 * Calculate the position of a session in the time grid from the start and end times
 * @param {*} startTime - format expected 'HH:mm'
 * @param {*} endTime - format expected 'HH:mm'
 * @param {*} startDayHour
 * @returns
 */
// Minimum height in pixels to ensure short sessions display at least one line of content
const MIN_SESSION_HEIGHT = 50;

export function getSessionPosition(startTime, endTime, startDayHour) {
  const [startHourStr, startMinStr] = startTime.split(':');
  const [endHourStr, endMinStr] = endTime.split(':');

  const startHourNum = parseInt(startHourStr);
  const startMinNum = parseInt(startMinStr);
  const endHourNum = parseInt(endHourStr);
  const endMinNum = parseInt(endMinStr);

  const startOffsetHours = startHourNum - startDayHour + startMinNum / 60;
  const endOffsetHours = endHourNum - startDayHour + endMinNum / 60;

  const top = startOffsetHours * 200 + 5; // 200px per hour - 5px extra to place the box with some margin
  const rawHeight = (endOffsetHours - startOffsetHours) * 200 - 10;
  const height = Math.max(rawHeight, MIN_SESSION_HEIGHT);

  return { top, height };
}
